/**
 * HQ 문의 관리 (💬 문의) — 2026-07-27
 *
 * 왼쪽: 매장 목록 (답변 대기 먼저) / 오른쪽: 그 매장과의 연속 대화 + 답변 입력.
 * 답변을 그대로 전체 공지로 올리는 버튼 포함 — 한 매장 질문은 대개 다른 매장도 궁금하다.
 *
 * 참고: 대표는 텔레그램(파수꾼 봇)에서 답장해도 여기 그대로 반영된다. 이 화면은
 *   지난 대화를 훑거나 길게 답할 때 쓰는 보조 창구.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import hqSupportService, {
  HqSupportMessage,
  HqSupportThread,
} from '../services/hqSupportService';
import SupportAttachmentImage from '../../../components/common/SupportAttachmentImage';

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ko-KR', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const HqSupportPage: React.FC = () => {
  const [threads, setThreads] = useState<HqSupportThread[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [storeName, setStoreName] = useState('');
  const [messages, setMessages] = useState<HqSupportMessage[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeBody, setNoticeBody] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorContent, setErrorContent] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  // 대화 영역 — 매장을 고르거나 새 글이 오면 맨 아래(최신 글)로 내린다
  const scrollRef = useRef<HTMLDivElement>(null);

  // 첫 진입에만 자동 선택. 30초 폴링이 다시 고르면 폰에서 '← 목록' 이 되돌려진다.
  const autoPicked = useRef(false);

  const loadThreads = useCallback(async () => {
    try {
      const list = await hqSupportService.listThreads();
      setThreads(list);
      if (!autoPicked.current && list.length > 0) {
        autoPicked.current = true;
        setSelected((cur) => cur || list[0].organization_id);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadThread = useCallback(async (orgId: string) => {
    const { store_name, messages: msgs } = await hqSupportService.getThread(orgId);
    setStoreName(store_name);
    setMessages(msgs);
  }, []);

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 30000);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    if (selected) loadThread(selected);
  }, [selected, loadThread]);

  // ⭐ 2026-10-14 — 대화를 열면 맨 처음 글이 보이던 것을 최신 글이 보이게.
  //   (사진은 SupportAttachmentImage 가 보일 때만 받는다. 종전엔 사진 하나가 올 때마다
  //    아직 안 온 사진 요청을 처음부터 다시 보내, 10/14 에 127건이 몰려 서버 일꾼이 멈췄다)
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [selected, messages]);

  const send = async () => {
    const content = reply.trim();
    if (!content || !selected || sending) return;
    setSending(true);
    try {
      await hqSupportService.reply(selected, content);
      setReply('');
      await loadThread(selected);
      await loadThreads();
    } finally {
      setSending(false);
    }
  };

  const promote = async () => {
    if (!noticeTitle.trim() || !noticeBody.trim()) return;
    await hqSupportService.promoteToNotice(noticeTitle.trim(), noticeBody.trim());
    setNoticeOpen(false);
    setNoticeTitle('');
    setNoticeBody('');
    setToast('전체 매장 공지로 등록했습니다.');
    setTimeout(() => setToast(null), 3000);
  };

  const promoteError = async () => {
    if (!selected || !errorTitle.trim()) return;
    await hqSupportService.promoteToErrorReport(
      selected,
      errorTitle.trim(),
      errorContent.trim(),
    );
    setErrorOpen(false);
    setErrorTitle('');
    setErrorContent('');
    setToast('오류 게시판에 등록했습니다. 상태 추적·진단이 시작됩니다.');
    setTimeout(() => setToast(null), 4000);
  };

  /** 모달 열 때 마지막 점주 메시지를 기본값으로 채워 손이 덜 가게 */
  const openErrorModal = () => {
    const lastOwnerMsg = [...messages].reverse().find((m) => m.author_type === 'owner');
    const body = lastOwnerMsg?.content || '';
    setErrorTitle(body.replace(/\n/g, ' ').slice(0, 60));
    setErrorContent(
      lastOwnerMsg
        ? `${body}\n\n(문의 화면에서 등록 · 매장: ${storeName}${
            lastOwnerMsg.page_url ? ` · 화면: ${lastOwnerMsg.page_url}` : ''
          })`
        : '',
    );
    setErrorOpen(true);
  };

  return (
    <div className="sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">💬 문의</h1>
          <p className="text-sm text-gray-500 mt-1">
            매장에서 온 문의입니다. 텔레그램 답장도 여기에 그대로 반영됩니다.
          </p>
        </div>
        {toast && (
          <div className="text-sm bg-green-50 border border-green-200 text-green-700 rounded px-3 py-2">
            {toast}
          </div>
        )}
      </div>

      <div
        className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[420px]"
        style={{ height: 'calc(100dvh - 220px)' }}
      >
        {/* 매장 목록 — 폰에서는 대화를 열면 숨긴다 */}
        <div
          className={`bg-white border border-gray-200 rounded-lg overflow-y-auto ${
            selected ? 'hidden lg:block' : ''
          }`}
        >
          {loading && <div className="p-4 text-gray-400 text-sm">불러오는 중...</div>}
          {!loading && threads.length === 0 && (
            <div className="p-6 text-center text-gray-500 text-sm">아직 문의가 없습니다.</div>
          )}
          {threads.map((t) => (
            <button
              key={t.organization_id}
              type="button"
              onClick={() => setSelected(t.organization_id)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                selected === t.organization_id ? 'bg-blue-50' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900 truncate">{t.store_name}</span>
                {t.waiting_reply && (
                  <span className="text-[11px] bg-rose-500 text-white rounded-full px-2 py-0.5 flex-shrink-0">
                    답변 대기
                  </span>
                )}
                <span className="text-[11px] text-gray-400 ml-auto flex-shrink-0">
                  {formatTime(t.last_message_at)}
                </span>
              </div>
              <div className="text-xs text-gray-500 truncate mt-1">
                {t.last_message_by === 'hq' && '↩ '}
                {t.last_message_text}
              </div>
            </button>
          ))}
        </div>

        {/* 대화 */}
        <div
          className={`lg:col-span-2 flex-col bg-white border border-gray-200 rounded-lg overflow-hidden ${
            selected ? 'flex' : 'hidden lg:flex'
          }`}
        >
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              왼쪽에서 매장을 선택하세요
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-semibold text-gray-900">
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="lg:hidden text-sm font-normal text-gray-500 hover:text-gray-800"
                  >
                    ← 목록
                  </button>
                  {storeName}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openErrorModal}
                    className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
                  >
                    🐛 오류로 등록
                  </button>
                  <button
                    type="button"
                    onClick={() => setNoticeOpen(true)}
                    className="text-xs px-3 py-1.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  >
                    📢 공지로 올리기
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {messages.map((m) => {
                  const fromHq = m.author_type === 'hq';
                  return (
                    <div key={m.id} className={`flex ${fromHq ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] flex flex-col ${fromHq ? 'items-end' : 'items-start'}`}>
                        {!fromHq && (
                          <span className="text-xs text-gray-500 mb-1 px-1">
                            🙋 {m.author_name || '점주'}
                            {m.category && ` · ${m.category}`}
                          </span>
                        )}
                        <div
                          className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line leading-relaxed ${
                            fromHq
                              ? 'bg-indigo-600 text-white rounded-br-sm'
                              : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                          }`}
                        >
                          {m.content}
                          {m.attachments.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {m.attachments.map((a) => (
                                <SupportAttachmentImage
                                  key={a.id}
                                  id={a.id}
                                  filename={a.filename}
                                  createdAt={m.created_at}
                                  fetchUrl={hqSupportService.attachmentUrl}
                                  className="rounded-lg max-h-64 border border-black/10"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 mt-1 px-1">
                          {formatTime(m.created_at)}
                          {m.page_url && !fromHq && (
                            <span className="ml-2 text-gray-300" title={m.page_url}>
                              · 화면 {m.page_url.replace(/^https?:\/\/[^/]+/, '')}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-3 border-t border-gray-200 flex gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
                  }}
                  rows={2}
                  placeholder="답변을 입력하세요 (Ctrl+Enter 전송)"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="button"
                  onClick={send}
                  disabled={sending || !reply.trim()}
                  className="px-5 rounded-lg bg-indigo-600 text-white font-semibold text-sm disabled:bg-gray-300"
                >
                  {sending ? '전송중' : '답변'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 오류 등록 모달 */}
      {errorOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-5">
            <h3 className="font-bold text-lg mb-1">🐛 오류 게시판에 등록</h3>
            <p className="text-xs text-gray-500 mb-4">
              상태 추적(접수→해결)과 자동 진단이 붙습니다. 실제 버그일 때만 등록하세요.
            </p>
            <input
              value={errorTitle}
              onChange={(e) => setErrorTitle(e.target.value)}
              placeholder="한 줄 요약"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
            />
            <textarea
              value={errorContent}
              onChange={(e) => setErrorContent(e.target.value)}
              rows={6}
              placeholder="증상·재현 방법"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setErrorOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                취소
              </button>
              <button
                type="button"
                onClick={promoteError}
                disabled={!errorTitle.trim()}
                className="px-4 py-2 rounded bg-rose-600 text-white text-sm font-semibold disabled:bg-gray-300"
              >
                등록
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 공지 승격 모달 */}
      {noticeOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-5">
            <h3 className="font-bold text-lg mb-1">📢 전체 공지로 올리기</h3>
            <p className="text-xs text-gray-500 mb-4">
              모든 매장의 대시보드·공지사항에 표시됩니다.
            </p>
            <input
              value={noticeTitle}
              onChange={(e) => setNoticeTitle(e.target.value)}
              placeholder="공지 제목"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
            />
            <textarea
              value={noticeBody}
              onChange={(e) => setNoticeBody(e.target.value)}
              rows={6}
              placeholder="공지 내용 (답변 내용을 붙여넣어 다듬으세요)"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setNoticeOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                취소
              </button>
              <button
                type="button"
                onClick={promote}
                disabled={!noticeTitle.trim() || !noticeBody.trim()}
                className="px-4 py-2 rounded bg-indigo-600 text-white text-sm font-semibold disabled:bg-gray-300"
              >
                공지 등록
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HqSupportPage;
